import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RefreshCw, FileText, BookOpen, TrendingUp, Users, BarChart, DollarSign, Award, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { AnalysisNav } from "@/components/ui/analysis-nav";
import ReactMarkdown from "react-markdown";
import { RainbowButton } from "@/components/ui/rainbow-button";
import { StarsBackground } from "@/components/ui/stars-background";
import { motion } from "framer-motion";
import { fadeIn, slideIn } from "@/utils/motion";
import { PdfDownloadButton } from "./ui/pdf-download-button";
import { InvestmentMemoProgressCard } from "./ui/investment-memo-progress-card";
import { FullscreenProgress } from "./ui/fullscreen-progress";

const investmentMemoSteps = [
  "Channeling our inner Warren Buffett 🧙‍♂️",
  "Calculating if this is the next unicorn or just a pony 🦄",
  "Running the numbers through our crystal ball algorithm",
  "Checking if this passes the 'mom test' for investment",
  "Analyzing if founders can survive on ramen profitably 🍜",
  "Determining the 'Uber for X' score (lower is better)",
  "Evaluating the startup's meme potential for viral growth",
  "Calculating ROI in both dollars and karma points",
  "Checking if the pitch deck has enough rocket ship emojis 🚀",
  "Measuring disruption levels on the Silicon Valley scale",
  "Assessing if this will make VCs FOMO at 3am",
  "Running the Peter Thiel monopoly test",
  "Calculating how many Teslas the exit will buy",
  "Checking if the TAM slide uses 'trillion' at least once",
  "Determining if this startup can 10x or just 10-why",
  "Evaluating hockey stick growth trajectory angle 📈",
  "Analyzing if the founders have main character energy",
  "Checking for sufficient AI/ML/Blockchain buzzwords",
  "Calculating the Sequoia Capital excitement index",
  "Determining if this is a vitamin, painkiller, or addiction",
  "Measuring founder conviction on a scale of 1 to Elon",
  "Checking if the startup name is pronounceable after Series B",
  "Evaluating burn rate vs. world domination timeline",
  "Analyzing if VCs will fight over this deal",
  "Creating the ultimate investment thesis... almost done! ✨",
];

interface InvestmentMemoData {
  id: string;
  startup_id: string;
  content: string;
  status: "pending" | "completed" | "failed";
  created_at: string;
  updated_at: string;
}

interface MemoSection {
  title: string;
  content: string;
  type: "positive" | "negative" | "neutral" | "concerning";
  icon: React.ReactNode;
}

export function InvestmentMemo() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showGeneratingProgress, setShowGeneratingProgress] = useState(false);
  
  // Ref for the content container to be captured in PDF
  const contentRef = useRef<HTMLDivElement>(null);
  const [memo, setMemo] = useState<InvestmentMemoData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [credits, setCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [parsedSections, setParsedSections] = useState<MemoSection[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [prerequisites, setPrerequisites] = useState({
    pitchDeck: { completed: false, message: 'Upload a pitch deck' },
    teamInfo: { completed: false, message: 'Team information is present' },
    marketResearch: { completed: false, message: 'Market research has been conducted' },
    investorInterview: { completed: false, message: 'Investor interview is completed' }
  });

  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await api.get('/api/credits/balance');
      setCredits(response.data.credits);
    } catch (err) {
      console.error('Error fetching credits:', err);
    } finally {
      setCreditsLoading(false);
    }
  };

  const fetchMemo = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/investment-memo/${id}`);
      setMemo(response.data);
      setError(null);
    } catch (err) {
      if (!initialLoad) {
        setError("Failed to load investment memo");
      }
      setMemo(null);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const generateMemo = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      // Check if user has enough credits
      const accessCheck = await api.get('/api/credits/check-access/investment-memo');
      
      if (!accessCheck.data.can_access) {
        setError(accessCheck.data.message);
        return;
      }

      // Use credits for the feature
      const useCredits = await api.post('/api/credits/use-feature/investment-memo');
      
      if (!useCredits.data.success) {
        setError(useCredits.data.message);
        return;
      }

      const response = await api.post(`/api/investment-memo/${id}/generate`);
      setMemo(response.data);
      setShowGeneratingProgress(true);
      
      if (response.data.status === "pending") {
        pollMemoStatus();
      }
    } catch (err: any) {
      console.error("Error generating investment memo:", err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          "Failed to generate investment memo";
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const pollMemoStatus = async () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/api/investment-memo/${id}`);
        setMemo(response.data);
        
        if (response.data.status !== "pending") {
          clearInterval(pollInterval);
          // Fetch credits again to update balance
          fetchCredits();
        }
      } catch (err) {
        console.error("Error polling memo status:", err);
        clearInterval(pollInterval);
      }
    }, 5000);
    
    // Stop polling after 10 minutes
    setTimeout(() => clearInterval(pollInterval), 10 * 60 * 1000);
  };

  // Check prerequisites status
  const checkPrerequisites = async () => {
    try {
      if (!id) return;

      // For the analysis ID, we need to get the pitch deck ID first
      // Since we're in the InvestmentMemo component, we already have the analysis ID
      const analysisId = id;
      let pitchDeckCompleted = false;
      let teamCompleted = false;
      let marketCompleted = false;
      let interviewCompleted = false;

      // Check for analysis (pitch deck)
      try {
        const analysisResponse = await api.get(`/api/analysis/${analysisId}`);
        pitchDeckCompleted = !!analysisResponse.data;
        // Get company name from analysis
        if (analysisResponse.data && analysisResponse.data.company_name) {
          setCompanyName(analysisResponse.data.company_name);
        }
      } catch (error) {
        console.error('Error checking pitch deck:', error);
      }
      
      // Check for team info (founders)
      try {
        const teamResponse = await api.get(`/api/linkedin/profiles/${analysisId}`);
        teamCompleted = teamResponse.data.profiles.length > 0;
      } catch (error) {
        console.error('Error checking team info:', error);
      }
      
      // Check for market research
      try {
        // Get the specific market research for this analysis
        const marketResponse = await api.get(`api/market-research?id=${analysisId}`);
        // If we get a response without error, market research exists
        marketCompleted = !!marketResponse.data;
      } catch (error) {
        console.error('Error checking market research:', error);
        // If we get a 404, it means market research doesn't exist
        marketCompleted = false;
      }
      
      // Check for investor interview
      try {
        const interviewResponse = await api.get(`/api/interview/${analysisId}`);
        interviewCompleted = interviewResponse.data && 
                           (interviewResponse.data.status === 'completed' || 
                            interviewResponse.data.transcript);
      } catch (error) {
        console.error('Error checking investor interview:', error);
      }
      
      setPrerequisites({
        pitchDeck: { 
          completed: pitchDeckCompleted, 
          message: pitchDeckCompleted ? 'Pitch deck uploaded ✓' : 'Upload a pitch deck'
        },
        teamInfo: { 
          completed: teamCompleted, 
          message: teamCompleted ? 'Team information is present ✓' : 'Team information is required'
        },
        marketResearch: { 
          completed: marketCompleted, 
          message: marketCompleted ? 'Market research completed ✓' : 'Market research needs to be conducted'
        },
        investorInterview: { 
          completed: interviewCompleted, // Keep track of actual completion status
          message: interviewCompleted ? 'Investor interview completed ✓' : 'Investor interview is not completed'
        }
      });
    } catch (err) {
      console.error('Error checking prerequisites:', err);
      // If there's an error, we'll assume the prerequisites aren't met
    }
  };

  useEffect(() => {
    if (id) {
      fetchMemo();
      fetchCredits();
      checkPrerequisites();
    }
  }, [id]);

  // Parse memo content when it's loaded
  useEffect(() => {
    if (memo && memo.status === "completed" && memo.content) {
      const content = extractMarkdown(memo.content);
      const sections = parseMemoContent(content);
      setParsedSections(sections);
    }
  }, [memo]);

  const refreshMemo = async () => {
    await fetchMemo();
  };

  const extractMarkdown = (htmlContent: string) => {
    const match = htmlContent.match(/<div class='markdown-content'>([\s\S]*?)<\/div>/);
    return match ? match[1] : htmlContent;
  };

  // Parse memo content and categorize sections
  const parseMemoContent = (content: string) => {
    if (!content) return [];
    
    // Split the content by ## headers
    let sections = content.split(/(?=## )/);
    
    // Skip the first section if it contains the investment memo header
    let startIndex = 0;
    if (sections.length > 0 && 
        (sections[0].includes("Investment Memo:") || 
         sections[0].includes("Date:") || 
         sections[0].includes("Prepared for:") || 
         sections[0].includes("Prepared by:"))) {
      startIndex = 1;
    }
    
    // Remove any empty sections
    sections = sections.filter(section => {
      if (!section.trim()) return false;
      const headerMatch = section.match(/## (.*?)(?:\n|$)/);
      return headerMatch && headerMatch[1].trim() !== '';
    });
    
    const parsedSections: MemoSection[] = [];
    
    sections.slice(startIndex).forEach((section) => {
      // For the first section, check if it starts with a header
      const hasHeader = section.trim().startsWith('## ');
      
      // Extract the header text if it exists
      let title = '';
      let content = section;
      let type: "positive" | "negative" | "neutral" | "concerning" = "neutral";
      let icon = <FileText />;
      
      if (hasHeader) {
        const headerMatch = section.match(/## (.*?)(?:\n|$)/);
        if (headerMatch) {
          title = headerMatch[1].trim();
          content = section.replace(/## (.*?)(?:\n|$)/, '').trim();
          
          // Categorize sections based on title and content
          if (title.includes("Executive Summary")) {
            icon = <BookOpen />;
          } else if (title.includes("Company Overview") || title.includes("Business Model")) {
            icon = <TrendingUp />;
          } else if (title.includes("Team") || title.includes("Management")) {
            icon = <Users />;
          } else if (title.includes("Market") || title.includes("Industry")) {
            icon = <BarChart />;
          } else if (title.includes("Financial") || title.includes("Valuation")) {
            icon = <DollarSign />;
          }
          
          // Determine sentiment type
          // Special handling for Conclusion & Recommendation section
          if (title.includes("Conclusion") || title.includes("Recommendation")) {
            const contentLower = content.toLowerCase();
            if (contentLower.includes("recommendation is pass") || 
                contentLower.includes("recommendation: pass") ||
                contentLower.includes("recommending pass") ||
                contentLower.includes("decline this opportunity")) {
              type = "negative";
            } else if (contentLower.includes("recommendation is invest") || 
                      contentLower.includes("recommendation: invest") ||
                      contentLower.includes("proceed with investment")) {
              type = "positive";
            } else if (contentLower.includes("recommendation is follow") || 
                      contentLower.includes("recommendation: follow") ||
                      contentLower.includes("continue monitoring")) {
              type = "neutral";
            }
          } else if (content.toLowerCase().includes("positive") || 
              content.toLowerCase().includes("strength") || 
              content.toLowerCase().includes("opportunity")) {
            type = "positive";
          } else if (content.toLowerCase().includes("negative") || 
                   content.toLowerCase().includes("weakness") || 
                   content.toLowerCase().includes("threat")) {
            type = "negative";
          } else if (content.toLowerCase().includes("concern") || 
                   content.toLowerCase().includes("risk")) {
            type = "concerning";
          }
          
          parsedSections.push({
            title,
            content,
            type,
            icon
          });
        }
      }
    });
    
    return parsedSections;
  };

  const renderMarkdownSections = (content: string) => {
    if (!content) return null;
    
    // Split the content by ## headers
    let sections = content.split(/(?=## )/);
    
    // Skip the first section if it contains the investment memo header
    let startIndex = 0;
    if (sections.length > 0 && 
        (sections[0].includes("Investment Memo:") || 
         sections[0].includes("Date:") || 
         sections[0].includes("Prepared for:") || 
         sections[0].includes("Prepared by:"))) {
      startIndex = 1;
    }
    
    // Remove any empty sections
    sections = sections.filter(section => {
      if (!section.trim()) return false;
      const headerMatch = section.match(/## (.*?)(?:\n|$)/);
      return headerMatch && headerMatch[1].trim() !== '';
    });
    
    return sections.slice(startIndex).map((section, index) => {
      // For the first section, check if it starts with a header
      const hasHeader = section.trim().startsWith('## ');
      
      // Extract the header text if it exists
      let header = '';
      let body = section;
      let type: "positive" | "negative" | "neutral" | "concerning" = "neutral";
      
      if (hasHeader) {
        const headerMatch = section.match(/## (.*?)(?:\n|$)/);
        if (headerMatch) {
          header = headerMatch[1].trim();
          body = section.replace(/## (.*?)(?:\n|$)/, '').trim();
          
          // Determine sentiment type based on content
          if (body.toLowerCase().includes("positive") || 
              body.toLowerCase().includes("strength") || 
              body.toLowerCase().includes("opportunity")) {
            type = "positive";
          } else if (body.toLowerCase().includes("negative") || 
                   body.toLowerCase().includes("weakness") || 
                   body.toLowerCase().includes("threat")) {
            type = "negative";
          } else if (body.toLowerCase().includes("concern") || 
                   body.toLowerCase().includes("risk")) {
            type = "concerning";
          }
          
          // Check header for specific sections
          if (header.includes("Executive Summary") || header.includes("1.")) {
            // Keep neutral for executive summary
          } else if (header.includes("Company Overview") || header.includes("2.")) {
            if (body.toLowerCase().includes("concern")) type = "concerning";
          } else if (header.includes("Problem") || header.includes("3.")) {
            if (body.toLowerCase().includes("concern")) type = "concerning";
          } else if (header.includes("Market") || header.includes("4.")) {
            if (body.toLowerCase().includes("concern")) type = "concerning";
          } else if (header.includes("Competitive") || header.includes("5.")) {
            if (!body.toLowerCase().includes("concern")) type = "neutral";
          } else if (header.includes("Conclusion") || header.includes("Recommendation") || header.includes("11.")) {
            // Special handling for Conclusion & Recommendation section
            const bodyLower = body.toLowerCase();
            if (bodyLower.includes("recommendation is pass") || 
                bodyLower.includes("recommendation: pass") ||
                bodyLower.includes("recommending pass") ||
                bodyLower.includes("decline this opportunity")) {
              type = "negative";
            } else if (bodyLower.includes("recommendation is invest") || 
                      bodyLower.includes("recommendation: invest") ||
                      bodyLower.includes("proceed with investment")) {
              type = "positive";
            } else if (bodyLower.includes("recommendation is follow") || 
                      bodyLower.includes("recommendation: follow") ||
                      bodyLower.includes("continue monitoring")) {
              type = "neutral";
            }
          }
        }
      }
      
      return (
        <motion.div
          key={index}
          variants={fadeIn("up", "spring", 0.3 + index * 0.2, 0.75)}
          initial="hidden"
          animate="show"
          className="w-full mb-6"
        >
          <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
            <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
              <StarsBackground />
            </div>
            <div className="relative z-10">
              {header && (
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center text-left">
                    <FileText className="mr-2 text-blue-500" size={26} />
                    {header}
                  </h2>
                  {renderSentimentBadge(type)}
                </div>
              )}
              <div className="border-t border-primary/20 pt-4">
                <div className="space-y-4 text-left">
                  <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 hover:prose-a:text-blue-800">
                    <ReactMarkdown>{body}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      );
    });
  };

  // Helper function to render status badge
  const renderStatusBadge = (status: "pending" | "completed" | "failed") => {
    if (status === "pending") {
      return (
        <div className="flex items-center text-amber-500 bg-amber-50 px-3 py-1 rounded-full text-xs font-medium">
          <Clock className="h-3 w-3 mr-1" />
          Processing
        </div>
      );
    } else if (status === "failed") {
      return (
        <div className="flex items-center text-red-500 bg-red-50 px-3 py-1 rounded-full text-xs font-medium">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Failed
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-green-500 bg-green-50 px-3 py-1 rounded-full text-xs font-medium">
          <CheckCircle className="h-3 w-3 mr-1" />
          Complete
        </div>
      );
    }
  };

  // Helper function to render sentiment badge
  const renderSentimentBadge = (type: "positive" | "negative" | "neutral" | "concerning") => {
    if (type === "positive") {
      return (
        <div className="flex items-center text-green-500 bg-green-50 px-3 py-1 rounded-full text-xs font-medium">
          <CheckCircle className="h-3 w-3 mr-1" />
          Positive
        </div>
      );
    } else if (type === "negative") {
      return (
        <div className="flex items-center text-red-500 bg-red-50 px-3 py-1 rounded-full text-xs font-medium">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Negative
        </div>
      );
    } else if (type === "concerning") {
      return (
        <div className="flex items-center text-amber-500 bg-amber-50 px-3 py-1 rounded-full text-xs font-medium">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Concerning
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-blue-500 bg-blue-50 px-3 py-1 rounded-full text-xs font-medium">
          <FileText className="h-3 w-3 mr-1" />
          Neutral
        </div>
      );
    }
  };

  // Render dashboard cards for key insights
  const renderDashboard = () => {
    // Find executive summary section
    const executiveSummary = parsedSections.find(section => 
      section.title.includes("Executive Summary"));
    
    // Get counts of different sentiment types
    const positiveCount = parsedSections.filter(s => s.type === "positive").length;
    const negativeCount = parsedSections.filter(s => s.type === "negative").length;
    const concerningCount = parsedSections.filter(s => s.type === "concerning").length;
    
    // Calculate sentiment score (simple version)
    const totalSections = parsedSections.length || 1; // Avoid division by zero
    const sentimentScore = Math.round(((positiveCount * 100) - (negativeCount * 50) - (concerningCount * 25)) / totalSections);
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Executive Summary Card */}
        <motion.div
          variants={fadeIn("up", "spring", 0.1, 0.75)}
          initial="hidden"
          animate="show"
          className="col-span-1 md:col-span-2 lg:col-span-3"
        >
          <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
            <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
              <StarsBackground />
            </div>
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center text-left">
                  <Award className="mr-2 text-blue-500" size={26} />
                  Investment Recommendation
                </h2>
                {memo && renderStatusBadge(memo.status)}
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Last updated: {new Date(memo?.updated_at || '').toLocaleString()}
              </p>
              
              <div className="border-t border-primary/20 pt-4">
                {executiveSummary ? (
                  <div className="space-y-4">
                    <div className="prose prose-sm max-w-none mb-4">
                      <ReactMarkdown>{executiveSummary.content.split('\n\n')[0]}</ReactMarkdown>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-blue-600">{sentimentScore > 0 ? '+' : ''}{sentimentScore}</div>
                        <div className="text-xs text-gray-500">Sentiment Score</div>
                      </div>
                      <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-green-600">{positiveCount}</div>
                        <div className="text-xs text-gray-500">Strengths</div>
                      </div>
                      <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-red-600">{negativeCount}</div>
                        <div className="text-xs text-gray-500">Weaknesses</div>
                      </div>
                      <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-amber-600">{concerningCount}</div>
                        <div className="text-xs text-gray-500">Concerns</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-700">Executive summary not available.</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
        
        {/* Key Highlights Cards */}
        {parsedSections
          .filter(section => section.title && section.title.trim() !== '')
          .slice(0, 6)
          .map((section, index) => (
          <motion.div
            key={index}
            variants={fadeIn("up", "spring", 0.2 + (index * 0.1), 0.75)}
            initial="hidden"
            animate="show"
          >
            <Card className="h-full p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
              <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                <StarsBackground />
              </div>
              <div className="relative z-10">
                <div className="flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      {section.icon}
                      <span className="ml-2">{section.title}</span>
                    </h3>
                  </div>
                  <div className="border-t border-primary/20 pt-4">
                    {renderSentimentBadge(section.type)}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Stars Background */}
      <div className="fixed inset-0 -z-10">
        <StarsBackground />
      </div>
      
      {/* Content */}
      <div className="relative z-10">
        <AnalysisNav id={id || ""} />
        
        <motion.div 
          variants={slideIn("left", "tween", 0.2, 1)}
          initial="hidden"
          animate="show"
          className="max-w-6xl mx-auto px-4 py-8"
          ref={contentRef}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-2">
              {memo && memo.status === "completed" && (
                <Button variant="outline" onClick={refreshMemo} disabled={loading} className="bg-white/10 backdrop-blur-sm">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Refresh
                </Button>
              )}
            </div>
            {memo && memo.status === "completed" && (
              <PdfDownloadButton
                contentRef={contentRef}
                fileName="Investment_Memo"
                documentTitle="Investment Memo"
                documentSubject="Comprehensive Investment Analysis"
                loading={pdfLoading}
                setLoading={setPdfLoading}
                onError={(error) => console.error('Error generating PDF:', error)}
                position="right"
              />
            )}
          </div>

          {error && (
            <motion.div 
              variants={fadeIn("up", "spring", 0.1, 0.75)}
              initial="hidden"
              animate="show"
              className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6"
            >
              <h3 className="text-lg font-medium">Error</h3>
              <p>{error}</p>
              <p className="text-sm mt-2">Please try again or contact support if the issue persists.</p>
            </motion.div>
          )}

          {loading && initialLoad ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : memo || showGeneratingProgress ? (
            <div className="space-y-4">
              {(memo?.status === "pending" || showGeneratingProgress) ? (
                <div className="relative min-h-[800px]">
                  <FullscreenProgress
                    status={memo?.status === "pending" ? "pending" : "processing"}
                    title={memo?.status === "pending" ? "Preparing Investment Analysis" : "Crafting Your Investment Memo"}
                    subtitle="Our AI is creating a comprehensive investment thesis 📈"
                    steps={investmentMemoSteps}
                    stepDuration={4500}
                    progressIncrement={{ min: 0.8, max: 2 }}
                    progressInterval={3000}
                    completionThreshold={95}
                    onComplete={() => {
                      // Refresh to get the completed memo
                      setShowGeneratingProgress(false);
                      refreshMemo();
                    }}
                  />
                </div>
              ) : memo?.status === "failed" ? (
                <motion.div 
                  variants={fadeIn("up", "spring", 0.2, 0.75)}
                  initial="hidden"
                  animate="show"
                  className="text-center py-8"
                >
                  <p>Failed to generate the investment memo. Please try again.</p>
                  <RainbowButton onClick={generateMemo} className="mt-4">
                    Retry Generation
                  </RainbowButton>
                </motion.div>
              ) : memo ? (
                <>
                  {/* Dashboard View */}
                  {renderDashboard()}
                  
                  {/* Detailed Analysis View */}
                  <div className="space-y-16">
                    {/* Original full markdown content */}
                    {renderMarkdownSections(extractMarkdown(memo.content))}
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <motion.div
              variants={fadeIn("up", "spring", 0.2, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <div className="text-center py-8">
                    {error && (
                      <div className="text-red-500 mb-4">{error}</div>
                    )}
                    {!memo && (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <FileText className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">Before you generate an investment memo</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                Make sure you have completed the following prerequisites:
              </p>
              
              <div className="w-full max-w-md bg-white rounded-lg p-4 mb-6 text-left">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    {prerequisites.pitchDeck.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={prerequisites.pitchDeck.completed ? "text-green-700" : "text-amber-700"}>
                      1. {prerequisites.pitchDeck.message}
                    </span>
                  </li>
                  <li className="flex items-start">
                    {prerequisites.teamInfo.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={prerequisites.teamInfo.completed ? "text-green-700" : "text-amber-700"}>
                      2. {prerequisites.teamInfo.message}
                    </span>
                  </li>
                  <li className="flex items-start">
                    {prerequisites.marketResearch.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={prerequisites.marketResearch.completed ? "text-green-700" : "text-amber-700"}>
                      3. {prerequisites.marketResearch.message}
                    </span>
                  </li>
                  <li className="flex items-start">
                    {prerequisites.investorInterview.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={prerequisites.investorInterview.completed ? "text-green-700" : "text-amber-700"}>
                      4. {prerequisites.investorInterview.message}
                    </span>
                  </li>
                </ul>
              </div>
              

              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {error}
                </div>
              )}
            </div>
          )}
                    <RainbowButton 
                      onClick={generateMemo} 
                      disabled={generating || creditsLoading || credits < 10 || 
                       (!prerequisites.pitchDeck.completed || 
                        !prerequisites.teamInfo.completed || 
                        !prerequisites.marketResearch.completed)}
                      className={`${credits < 10 || (!prerequisites.pitchDeck.completed || 
                        !prerequisites.teamInfo.completed || 
                        !prerequisites.marketResearch.completed) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {generating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : creditsLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : credits < 10 ? (
                        <div className="flex items-center justify-center">
                          <span className="text-red-500">Need More Credits</span>
                        </div>
                      ) : (
                        <>
                          <BookOpen className="mr-2 h-4 w-4" />
                          Generate Investment Memo (10 Credits)
                        </>
                      )}
                    </RainbowButton>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
} 